import { IconInput } from "../components/Icon";
import { Permissions, Permission } from "../account/permissions";

/**
 * The whole information architecture, as data.
 *
 * This is ScadsysIdentity's single best pattern (organizations/Index.tsx declares its `Tabs` array
 * with route + icon + label + description + required permission, then feeds the sidebar, the landing
 * card grid and the nested route table from it). One declaration, three surfaces, no chance of the
 * nav and the dashboard disagreeing about what a user can reach.
 *
 * `permission` gates the entry. Omit it for something everyone signed in can see.
 */

export interface NavEntry {
    route: string;
    name: string;
    description: string;
    icon: IconInput;
    permission?: Permission;
    /** Hidden from the sidebar and dashboard, but still routable (detail pages, dev tools). */
    hidden?: boolean;
}

export interface NavGroup {
    /** Sidebar section heading. */
    name: string;
    icon: IconInput;
    entries: NavEntry[];
}

export const NAV: NavGroup[] = [
    {
        name: "Marks",
        icon: ["fad", "pen-to-square"],
        entries: [
            {
                route: "/marks",
                name: "Mark entry",
                description: "Your subjects, their divisions and analytics, and a mark sheet per division",
                icon: ["fad", "table-list"],
                permission: Permissions.MARKS_READ,
            },
            {
                route: "/calculations",
                name: "Calculations",
                description: "Preview and run the calculation engine over a scope",
                icon: ["fad", "calculator"],
                permission: Permissions.CALCULATIONS_RUN,
            },
        ],
    },
    {
        name: "Assessments",
        icon: ["fad", "list-check"],
        entries: [
            {
                route: "/curriculum-reporting",
                name: "Curriculum reporting",
                description: "Map curriculum definitions, select reporting results and capture immutable snapshots",
                icon: ["fad", "chart-column"],
                permission: Permissions.CONFIGURATION_READ,
            },
            {
                route: "/mark-imports",
                name: "Mark imports",
                description: "Received marks, source revisions and calculation status",
                icon: ["fad", "file-import"],
                permission: Permissions.MARKS_READ,
            },
            {
                route: "/mark-imports/connect",
                name: "Connect an application",
                description: "Choose an assessment target for an application sending marks",
                icon: ["fad", "plug"],
                permission: Permissions.CONFIGURATION_READ,
                hidden: true,
            },
            {
                route: "/activities",
                name: "Assessment structure",
                description: "Build the group tree and assessments inside a qualification module",
                icon: ["fad", "sitemap"],
                permission: Permissions.ACTIVITIES_READ,
            },
            {
                route: "/configuration",
                name: "Configuration",
                description: "Mark types, assessment types, group types and comment types",
                icon: ["fad", "sliders"],
                permission: Permissions.CONFIGURATION_READ,
            },
            {
                route: "/grading",
                name: "Grade scales",
                description: "Symbol bands, and the default scale for each mark type",
                icon: ["fad", "award"],
                permission: Permissions.GRADE_SCALES_READ,
            },
        ],
    },
    {
        name: "Procedures",
        icon: ["fad", "diagram-project"],
        entries: [
            {
                route: "/procedures",
                name: "Calculation procedures",
                description: "Author, validate and publish the rules that produce a mark",
                icon: ["fad", "diagram-project"],
                permission: Permissions.PROCEDURES_READ,
            },
            {
                route: "/assignments",
                name: "Assignments",
                description: "Bind a published procedure version to a qualification module",
                icon: ["fad", "link"],
                permission: Permissions.PROCEDURES_READ,
            },
            {
                route: "/findings",
                name: "Findings",
                description: "Calculation coverage, warnings and errors, computed on demand",
                icon: ["fad", "triangle-exclamation"],
                permission: Permissions.PROCEDURES_READ,
            },
        ],
    },
    {
        name: "Structure",
        icon: ["fad", "building"],
        entries: [
            {
                route: "/structure/faculties",
                name: "Faculties",
                description: "The top of the academic catalogue",
                icon: ["fad", "building-columns"],
                permission: Permissions.STRUCTURE,
            },
            {
                route: "/structure/schools",
                name: "Schools",
                description: "Academic schools and departments within a faculty",
                icon: ["fad", "school"],
                permission: Permissions.STRUCTURE,
            },
            {
                route: "/structure/qualifications",
                name: "Qualifications",
                description: "Programmes offered by a school",
                icon: ["fad", "graduation-cap"],
                permission: Permissions.STRUCTURE,
            },
            {
                route: "/structure/qualification-levels",
                name: "Qualification levels",
                description: "Year and grade levels within a qualification",
                icon: ["fad", "layer-group"],
                permission: Permissions.STRUCTURE,
            },
            {
                route: "/structure/modules",
                name: "Modules",
                description: "Subjects, independent of any particular year",
                icon: ["fad", "book"],
                permission: Permissions.STRUCTURE,
            },
        ],
    },
    {
        name: "Management",
        icon: ["fad", "calendar-days"],
        entries: [
            {
                route: "/management/academic-years",
                name: "Academic years",
                description: "The years the institution runs",
                icon: ["fad", "calendar"],
                permission: Permissions.MANAGEMENT,
            },
            {
                route: "/management/cycles",
                name: "Assessment cycles",
                description: "Terms and semesters within an academic year",
                icon: ["fad", "calendar-week"],
                permission: Permissions.MANAGEMENT,
            },
            {
                route: "/management/academic-programs",
                name: "Academic programs",
                description: "Programme definitions spanning academic years",
                icon: ["fad", "rectangle-list"],
                permission: Permissions.MANAGEMENT,
            },
            {
                route: "/management/program-levels",
                name: "Program levels",
                description: "A qualification level offered in a given year and programme",
                icon: ["fad", "layer-group"],
                permission: Permissions.MANAGEMENT,
            },
            {
                route: "/management/qualification-modules",
                name: "Qualification modules",
                description: "A subject for one cohort in one cycle - what the engine calculates against",
                icon: ["fad", "cubes"],
                permission: Permissions.MANAGEMENT,
            },
            {
                route: "/management/teaching-groups",
                name: "Teaching groups",
                description: "Class and register groups within a qualification module",
                icon: ["fad", "users"],
                permission: Permissions.MANAGEMENT,
            },
        ],
    },
    {
        name: "Enrollment",
        icon: ["fad", "user-plus"],
        entries: [
            {
                route: "/enrollment/subject-choices",
                name: "Subject choices",
                description: "Choose subjects for a whole grade at once, across every term",
                icon: ["fad", "list-check"],
                permission: Permissions.ENROLLMENT,
            },
            {
                route: "/enrollment/subject-choice-rules",
                name: "Subject choice rules",
                description: "Compulsory subjects, either/or choices, elective blocks and totals for each grade",
                icon: ["fad", "scale-balanced"],
                // Rules describe the curriculum, so they are authored under the structure permission.
                permission: Permissions.STRUCTURE,
            },
            {
                route: "/enrollment/modules",
                name: "Module enrollment",
                description: "Which students are taking which qualification modules",
                icon: ["fad", "user-check"],
                permission: Permissions.ENROLLMENT,
            },
            {
                route: "/enrollment/levels",
                name: "Level enrollment",
                description: "Student placement into a qualification program level",
                icon: ["fad", "user-graduate"],
                permission: Permissions.ENROLLMENT,
            },
            {
                route: "/enrollment/teaching-groups",
                name: "Teaching group members",
                description: "Students and educators attached to a class group",
                icon: ["fad", "users-gear"],
                permission: Permissions.ENROLLMENT,
            },
        ],
    },
    {
        name: "Insight",
        icon: ["fad", "chart-pie"],
        entries: [
            {
                route: "/reports",
                name: "Reports",
                description: "Templates, preview, and HTML or PDF rendering",
                icon: ["fad", "file-lines"],
                permission: Permissions.REPORTS_READ,
            },
            {
                route: "/audit",
                name: "Audit trail",
                description: "Who changed what, and when",
                icon: ["fad", "clock-rotate-left"],
                permission: Permissions.AUDIT_READ,
            },
            {
                route: "/integrations",
                name: "Integrations",
                description: "External system connections and the legacy SCADSYS import",
                icon: ["fad", "plug"],
                permission: Permissions.INTEGRATIONS_EXECUTE,
            },
        ],
    },
];

/** Flat list of every entry, for route generation and lookups. */
export const ALL_ENTRIES: NavEntry[] = NAV.flatMap((g) => g.entries);

export interface PermissionHolder {
    hasPermission: (permission: string) => boolean;
}

export function visibleEntries(group: NavGroup, user: PermissionHolder): NavEntry[] {
    return group.entries.filter((e) => !e.hidden && (!e.permission || user.hasPermission(e.permission)));
}

export function visibleGroups(user: PermissionHolder): Array<{ group: NavGroup; entries: NavEntry[] }> {
    return NAV.map((group) => ({ group, entries: visibleEntries(group, user) })).filter((g) => g.entries.length > 0);
}
